import { type PortableTextBlock } from "sanity"

// Per-glyph widths (in px) for Trade Gothic Text at 16px / 300 weight.
// Unlisted characters fall back to DEFAULT_WIDTH, which matches the average
// advance width for lowercase letters, digits, and common punctuation at this
// size/weight.
const CHAR_WIDTHS: Record<string, number> = {
    " ": 4,
    "!": 4,
    '"': 6,
    "#": 8,
    $: 8,
    "%": 12,
    "&": 10,
    "'": 3,
    "(": 5,
    ")": 5,
    "*": 6,
    "+": 8,
    ",": 4,
    "-": 5,
    ".": 4,
    "/": 5,
    "0": 8,
    "1": 5,
    "2": 8,
    "3": 8,
    "4": 8,
    "5": 8,
    "6": 8,
    "7": 8,
    "8": 8,
    "9": 8,
    ":": 4,
    ";": 4,
    "<": 8,
    "=": 8,
    ">": 8,
    "?": 7,
    "@": 13,
    I: 4,
    J: 6,
    L: 8,
    M: 13,
    T: 8,
    W: 14,
    "[": 5,
    "\\": 5,
    "]": 5,
    "^": 6,
    _: 8,
    "`": 3,
    c: 7,
    f: 5,
    i: 4,
    j: 4,
    l: 4,
    m: 12,
    r: 5,
    s: 7,
    t: 5,
    v: 8,
    w: 11,
    x: 7,
    y: 8,
    z: 7,
    "{": 5,
    "|": 3,
    "}": 5,
    "~": 8
}

const DEFAULT_WIDTH = 8
const SPACE_WIDTH = CHAR_WIDTHS[" "] ?? 4

function measureWord(word: string): number {
    let total = 0
    for (const char of word) total += CHAR_WIDTHS[char] ?? DEFAULT_WIDTH
    return total
}

function countLinesForSegment(text: string, maxWidthPx: number): number {
    const words = text.split(/\s+/).filter(Boolean)
    if (words.length === 0) return 0

    let lines = 1
    let lineWidth = 0
    for (const word of words) {
        const wordWidth = measureWord(word)
        const needsSpace = lineWidth > 0
        const candidate = lineWidth + (needsSpace ? SPACE_WIDTH : 0) + wordWidth
        if (candidate > maxWidthPx && lineWidth > 0) {
            lines++
            lineWidth = wordWidth
        } else {
            lineWidth = candidate
        }
    }
    return lines
}

function portableTextToLinesPerBlock(richText: PortableTextBlock[]): string[] {
    return richText
        .map(block => {
            const children = (block.children as { text?: string }[]) ?? []
            return children.map(c => c.text ?? "").join("")
        })
        .filter(block => block.trim().length > 0)
}

// Estimates how many lines a plain string or PortableText value will render to
// at a given width, simulating word-wrapping using approximated Trade Gothic
// Text 16px glyph widths. Each PortableText block contributes its own lines
// (paragraph breaks always start a new line).
export function estimateLineCount(
    input: { text?: string; richText?: PortableTextBlock[] },
    maxWidthPx: number
): number {
    const { text, richText } = input
    if (text) return countLinesForSegment(text, maxWidthPx)
    if (richText?.length) {
        const segments = portableTextToLinesPerBlock(richText)
        return segments.reduce(
            (total, segment) => total + countLinesForSegment(segment, maxWidthPx),
            0
        )
    }
    return 0
}

// Returns the widest width (in px) at which the text still renders in more
// than `maxLines` lines. At any width ≤ the returned value the text is
// guaranteed to overflow; above it, it fits. Returns 0 when the text fits in
// `maxLines` even at a very narrow width (i.e. it fits everywhere). Relies on
// line count being monotonically non-increasing with width, which holds for
// ordinary word-wrap.
export function findMaxOverflowWidth(
    input: { text?: string; richText?: PortableTextBlock[] },
    maxLines: number,
    {
        minWidthPx = 64,
        maxWidthPx = 2048
    }: { minWidthPx?: number; maxWidthPx?: number } = {}
): number {
    if (estimateLineCount(input, maxWidthPx) > maxLines) return maxWidthPx
    if (estimateLineCount(input, minWidthPx) <= maxLines) return 0

    let lo = minWidthPx
    let hi = maxWidthPx
    while (lo < hi) {
        const mid = Math.floor((lo + hi + 1) / 2)
        if (estimateLineCount(input, mid) > maxLines) {
            lo = mid
        } else {
            hi = mid - 1
        }
    }
    return lo
}
